/**
 * Прайс-лист: просмотр и ПРАВКА прямо в окне — как наряд.
 *
 * Прайс собирается из справочника конструкций, но бумага, которую отдают
 * клинике, бывает нужна другой: убрать строку, дописать позицию, которой нет
 * в справочнике, поправить описание или цену для конкретного случая. Раньше
 * ради этого прайс правили в Word заново.
 *
 * Правки живут ТОЛЬКО в этом листе: печать и PDF берут то, что на экране, а
 * справочник и цены в заказах остаются прежними. Кнопка «Как в справочнике»
 * возвращает лист к собранному из справочника.
 *
 * Столбцы цен включаются по отдельности: клинике по договору незачем видеть
 * наличные цены, и наоборот. Позиции убираются ГАЛОЧКОЙ, а не удалением —
 * убранная остаётся в листе бледной строкой, и вернуть её можно одним
 * щелчком. Крестик по-прежнему удаляет строку насовсем.
 */

import { useEffect, useMemo, useState } from 'react';
import { Download, Plus, Printer, RotateCcw, Save, Send, X } from 'lucide-react';
import {
  formatKop,
  PRICE_LIST_HEADERS,
  PRICE_LIST_TERMS,
  PRICE_LIST_TITLE,
  priceListColumns,
  renderPriceListPage,
  rublesToKop,
  type PriceListModel,
  type PriceListRow,
  type PriceListSection,
} from '@otc/shared';
import { api, downloadPriceList } from '../api/client.js';
import { Button, Modal, Spinner } from './ui.js';
import { FitToWidth } from './FitToWidth.js';
import { printHtml } from './printHtml.js';
import { ReportEditToggle } from './ReportSheet.js';
import { SendPriceListDialog } from './SendDialogs.js';

type FullModel = Required<PriceListModel>;

/** Модель со всеми полями: правке незачем разбираться с «не задано». */
function complete(model: PriceListModel): FullModel {
  return {
    ...model,
    title: model.title ?? PRICE_LIST_TITLE,
    terms: model.terms ?? PRICE_LIST_TERMS,
    headers: model.headers ?? [...PRICE_LIST_HEADERS],
    columns: priceListColumns(model),
  };
}

const EMPTY_ROW: PriceListRow = { name: '', description: '', priceCashKop: 0, priceContractKop: 0 };

export function PriceListModal({
  onClose,
  onError,
  onSaved,
  onNotify,
  saved,
}: {
  onClose: () => void;
  onError: (message: string) => void;
  /** Лист сохранён — в «Документы» или поверх сохранённого. */
  onSaved: (message: string) => void;
  /**
   * Сказать что-то, НЕ трогая сам лист.
   *
   * Для отправки письма: она ничего в прайсе не меняет. Раньше здесь звался
   * `onSaved`, а в «Конструкциях» он закрывает окно — и прайс захлопывался
   * сразу после того, как письма ушли. Без обработчика весть просто теряется,
   * поэтому по умолчанию она идёт тем же путём, что и раньше.
   */
  onNotify?: (message: string) => void;
  /** Лист, открытый из «Документов»; без него прайс собирается из справочника. */
  saved?: { id: string; model: PriceListModel };
}) {
  const [original, setOriginal] = useState<FullModel | null>(null);
  const [draft, setDraft] = useState<FullModel | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Высота листа в просмотре: уточняется по содержимому после загрузки. */
  const [height, setHeight] = useState(1100);

  useEffect(() => {
    // Сохранённый лист открывается как есть — справочник его не меняет.
    if (saved) {
      const full = complete(saved.model);
      setOriginal(full);
      setDraft(full);
      return;
    }
    let cancelled = false;
    api
      .get<{ model: PriceListModel }>('/api/price-list/model')
      .then(({ model }) => {
        if (cancelled) return;
        const full = complete(model);
        setOriginal(full);
        setDraft(full);
      })
      .catch((error) => {
        onError(error instanceof Error ? error.message : 'Не удалось получить прайс-лист');
        onClose();
      });
    return () => {
      cancelled = true;
    };
    // Прайс берётся один раз при открытии окна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const html = useMemo(() => (draft ? renderPriceListPage(draft) : ''), [draft]);
  const dirty = Boolean(draft && original && JSON.stringify(draft) !== JSON.stringify(original));

  const update = (fn: (model: FullModel) => FullModel) => setDraft((model) => (model ? fn(model) : model));
  const patchPart = (pi: number, patch: Partial<FullModel['parts'][number]>) =>
    update((m) => ({ ...m, parts: m.parts.map((part, i) => (i === pi ? { ...part, ...patch } : part)) }));
  const patchSection = (pi: number, si: number, patch: Partial<PriceListSection>) =>
    update((m) => ({
      ...m,
      parts: m.parts.map((part, i) =>
        i !== pi ? part : { ...part, sections: part.sections.map((s, j) => (j === si ? { ...s, ...patch } : s)) },
      ),
    }));
  const patchRow = (pi: number, si: number, ri: number, patch: Partial<PriceListRow>, rows: PriceListRow[]) =>
    patchSection(pi, si, { rows: rows.map((row, k) => (k === ri ? { ...row, ...patch } : row)) });

  const download = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await downloadPriceList(draft);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Не удалось получить прайс-лист');
    } finally {
      setBusy(false);
    }
  };

  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      if (saved) {
        await api.patch(`/api/price-lists/${saved.id}`, { model: draft });
        setOriginal(draft);
        onSaved('Прайс-лист сохранён');
      } else {
        await api.post('/api/price-lists', { model: draft });
        onSaved('Прайс-лист сохранён — он в «Документы → Прайс-листы»');
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Не удалось сохранить прайс-лист');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full rounded border px-2 py-1 text-sm';
  const inputStyle = { borderColor: 'var(--otc-line)', background: 'var(--otc-surface)', color: 'var(--otc-ink)' };
  const addButton = 'mt-2 inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs';
  const addStyle = { borderColor: 'var(--otc-line)', color: 'var(--otc-accent)', background: 'var(--otc-surface)' };

  /* Окно выбора получателей. Открывается кнопкой «Отправить». */
  const [отправка, setОтправка] = useState(false);

  return (
    <>
    <Modal
      title="Прайс-лист"
      size="xl"
      onClose={onClose}
      footer={
        <>
          <Button onClick={() => void save()} disabled={!draft || saving || (Boolean(saved) && !dirty)}>
            <Save size={14} /> {saving ? 'Сохранение…' : saved ? 'Сохранить изменения' : 'Сохранить в «Документы»'}
          </Button>
          <Button variant="secondary" onClick={() => draft && printHtml(html)} disabled={!draft}>
            <Printer size={14} /> Печать
          </Button>
          <Button variant="secondary" onClick={() => void download()} disabled={!draft || busy}>
            <Download size={14} /> {busy ? 'Готовим…' : 'Скачать PDF'}
          </Button>
          {/*
            «Отправить» — тот же лист, что на экране, письмом.

            Вложение собирается из ЭТОГО черновика: с выбранными колонками
            цен и убранными позициями. Иначе врач получил бы не тот прайс,
            который ему показывали.
          */}
          <Button variant="secondary" onClick={() => setОтправка(true)} disabled={!draft}>
            <Send size={14} /> Отправить
          </Button>
          {dirty && (
            <Button variant="ghost" onClick={() => setDraft(original)}>
              <RotateCcw size={14} /> {saved ? 'Отменить правки' : 'Как в справочнике'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
          {dirty && (
            <span className="ml-auto self-center text-[11px]" style={{ color: 'var(--otc-amber)' }}>
              {saved ? 'Есть несохранённые правки' : 'Правки только в этом листе — справочник не меняется'}
            </span>
          )}
        </>
      }
    >
      {!draft ? (
        <Spinner />
      ) : (
        <>
          <ReportEditToggle
            editMode={editMode}
            onToggle={() => setEditMode((v) => !v)}
            narrow={false}
            note="Правки идут в печать и PDF и не меняют цены в справочнике"
          />

          {editMode ? (
            <div className="space-y-4 pb-2">
              <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
                <label className="block text-xs text-ink2">
                  Заголовок
                  <textarea
                    rows={2}
                    className={`${input} mt-1 resize-y`}
                    style={inputStyle}
                    value={draft.title}
                    onChange={(e) => update((m) => ({ ...m, title: e.target.value }))}
                  />
                </label>
                <label className="block text-xs text-ink2">
                  Цены действуют от
                  <input
                    className={`${input} mt-1`}
                    style={inputStyle}
                    value={draft.date}
                    onChange={(e) => update((m) => ({ ...m, date: e.target.value }))}
                  />
                </label>
              </div>

              <label className="block text-xs text-ink2">
                Сроки изготовления
                <textarea
                  rows={2}
                  className={`${input} mt-1 resize-y`}
                  style={inputStyle}
                  value={draft.terms}
                  onChange={(e) => update((m) => ({ ...m, terms: e.target.value }))}
                />
              </label>

              <div>
                <p className="mb-1 text-xs text-ink2">Колонки</p>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {draft.headers.map((header, hi) => {
                    /* Третья и четвёртая колонки — цены: их можно не печатать. */
                    const key = hi === 2 ? 'cash' : hi === 3 ? 'contract' : null;
                    const on = key ? draft.columns[key] : true;
                    /* Одна цена остаётся всегда: прайс без цен клинике ни к чему. */
                    const last = key !== null && on && !draft.columns[key === 'cash' ? 'contract' : 'cash'];
                    return (
                      <div key={hi}>
                        <input
                          className={`${input} font-medium`}
                          style={{ ...inputStyle, opacity: on ? 1 : 0.45 }}
                          value={header}
                          onChange={(e) =>
                            update((m) => ({ ...m, headers: m.headers.map((h, k) => (k === hi ? e.target.value : h)) }))
                          }
                        />
                        {key && (
                          <label
                            className="mt-1 flex items-center gap-1.5 text-xs text-ink2"
                            title={last ? 'Хотя бы одна цена должна остаться в листе' : undefined}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              disabled={last}
                              onChange={() =>
                                update((m) => ({ ...m, columns: { ...m.columns, [key]: !m.columns[key] } }))
                              }
                            />
                            Печатать
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {draft.parts.map((part, pi) => (
                <section key={pi} className="rounded-lg border p-3" style={{ borderColor: 'var(--otc-line)' }}>
                  <input
                    className={`${input} font-semibold`}
                    style={inputStyle}
                    value={part.title}
                    onChange={(e) => patchPart(pi, { title: e.target.value })}
                  />

                  {part.sections.map((section, si) => (
                    <div key={si} className="mt-3">
                      <div className="flex items-center gap-2">
                        {/* Вся группа разом: включить или убрать все её позиции. */}
                        <input
                          type="checkbox"
                          className="flex-shrink-0"
                          checked={section.rows.some((row) => !row.hidden)}
                          onChange={(e) =>
                            patchSection(pi, si, {
                              rows: section.rows.map((row) => ({ ...row, hidden: !e.target.checked })),
                            })
                          }
                          title="Вся группа в прайсе"
                          aria-label={`Группа «${section.title}» в прайсе`}
                        />
                        <input
                          className={`${input} font-medium`}
                          style={{ ...inputStyle, background: '#f6f3ec' }}
                          value={section.title}
                          onChange={(e) => patchSection(pi, si, { title: e.target.value })}
                        />
                        <button
                          onClick={() => patchPart(pi, { sections: part.sections.filter((_, j) => j !== si) })}
                          className="flex-shrink-0 rounded p-1 text-ink2 hover:bg-black/5"
                          title="Убрать группу целиком"
                          aria-label="Убрать группу"
                        >
                          <X size={14} />
                        </button>
                      </div>

                      <div className="mt-1 overflow-x-auto">
                        <table className="w-full min-w-[560px] text-sm">
                          <thead>
                            <tr className="text-left text-xs text-ink2">
                              {/* Галочка — ПЕРВОЙ: на телефоне её видно, не листая таблицу вбок. */}
                              <th className="w-7 px-1 py-1 font-normal" title="В прайсе" />
                              <th className="w-[28%] px-1 py-1 font-normal">{draft.headers[0]}</th>
                              <th className="px-1 py-1 font-normal">{draft.headers[1]}</th>
                              {draft.columns.cash && <th className="w-28 px-1 py-1 font-normal">{draft.headers[2]}</th>}
                              {draft.columns.contract && <th className="w-28 px-1 py-1 font-normal">{draft.headers[3]}</th>}
                              <th className="w-8" />
                            </tr>
                          </thead>
                          <tbody>
                            {section.rows.map((row, ri) => (
                              <tr key={ri} style={row.hidden ? { opacity: 0.45 } : undefined}>
                                <td className="p-1 pt-2 align-top">
                                  <input
                                    type="checkbox"
                                    checked={!row.hidden}
                                    onChange={() => patchRow(pi, si, ri, { hidden: !row.hidden }, section.rows)}
                                    title={row.hidden ? 'Вернуть в прайс' : 'Убрать из прайса'}
                                    aria-label={`«${row.name || 'Позиция'}» в прайсе`}
                                  />
                                </td>
                                <td className="p-1 align-top">
                                  <input
                                    className={input}
                                    style={inputStyle}
                                    value={row.name}
                                    onChange={(e) => patchRow(pi, si, ri, { name: e.target.value }, section.rows)}
                                  />
                                </td>
                                <td className="p-1 align-top">
                                  <textarea
                                    rows={1}
                                    className={`${input} resize-y`}
                                    style={inputStyle}
                                    value={row.description}
                                    onChange={(e) => patchRow(pi, si, ri, { description: e.target.value }, section.rows)}
                                  />
                                </td>
                                {draft.columns.cash && (
                                  <td className="p-1 align-top">
                                    <input
                                      className={`${input} tabular text-right`}
                                      style={inputStyle}
                                      value={formatKop(row.priceCashKop)}
                                      onChange={(e) =>
                                        patchRow(pi, si, ri, { priceCashKop: rublesToKop(e.target.value) }, section.rows)
                                      }
                                    />
                                  </td>
                                )}
                                {draft.columns.contract && (
                                  <td className="p-1 align-top">
                                    <input
                                      className={`${input} tabular text-right`}
                                      style={inputStyle}
                                      value={formatKop(row.priceContractKop)}
                                      onChange={(e) =>
                                        patchRow(pi, si, ri, { priceContractKop: rublesToKop(e.target.value) }, section.rows)
                                      }
                                    />
                                  </td>
                                )}
                                <td className="p-1 align-top">
                                  <button
                                    onClick={() => patchSection(pi, si, { rows: section.rows.filter((_, k) => k !== ri) })}
                                    className="rounded p-1 text-ink2 hover:bg-black/5"
                                    title="Удалить строку насовсем"
                                    aria-label="Удалить строку"
                                  >
                                    <X size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <button
                        onClick={() => patchSection(pi, si, { rows: [...section.rows, { ...EMPTY_ROW }] })}
                        className={addButton}
                        style={addStyle}
                      >
                        <Plus size={12} /> Строка
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={() =>
                      patchPart(pi, { sections: [...part.sections, { title: 'Новая группа', rows: [{ ...EMPTY_ROW }] }] })
                    }
                    className={`${addButton} ml-2`}
                    style={addStyle}
                  >
                    <Plus size={12} /> Группа
                  </button>
                </section>
              ))}

              <label className="block text-xs text-ink2">
                Подпись внизу листа
                <input
                  className={`${input} mt-1`}
                  style={inputStyle}
                  value={draft.performerName}
                  onChange={(e) => update((m) => ({ ...m, performerName: e.target.value }))}
                />
              </label>
            </div>
          ) : (
            /*
              Лист уменьшается ЦЕЛИКОМ, как напечатается: свёрстан под А4, и на
              ширине телефона вёрстка поехала бы. Высота — по содержимому.
            */
            <FitToWidth naturalWidth={794} className="mb-1 overflow-x-hidden">
              <iframe
                title="Прайс-лист"
                srcDoc={html}
                style={{ width: 794, height, border: 0, background: '#fff' }}
                onLoad={(event) => {
                  const doc = event.currentTarget.contentDocument;
                  if (doc) setHeight(doc.documentElement.scrollHeight + 16);
                }}
              />
            </FitToWidth>
          )}
        </>
      )}
    </Modal>

    {/* Окно выбора получателей: тот же лист, что на экране, уходит письмом. */}
    {отправка && draft && (
      <SendPriceListDialog
        model={draft}
        onClose={() => setОтправка(false)}
        onDone={(текст) => {
          setОтправка(false);
          (onNotify ?? onSaved)(текст);
        }}
      />
    )}
    </>
  );
}
